/**
 * Whether a specific Incoming Stock refresh target is running.
 * Uses per-target flags from GET /api/cache/status (poRefresh.activeTargets).
 */
export function isIncomingStockTargetRefreshing(cacheStatus, target) {
  const targets = cacheStatus?.poRefresh?.activeTargets
    || cacheStatus?.poCache?.activeTargets;
  if (targets && typeof targets[target] === 'boolean') {
    return targets[target];
  }
  return false;
}

export function isPlannerCycleRefreshing(cacheStatus) {
  return !!cacheStatus?.plannerRefresh?.isRefreshing;
}

/** Human-readable list of active Incoming Stock refresh targets. */
export function formatActivePoRefreshTargets(cacheStatus) {
  const targets = cacheStatus?.poRefresh?.activeTargets
    || cacheStatus?.poCache?.activeTargets;
  if (!cacheStatus?.poRefresh?.isRefreshing && !cacheStatus?.poCache?.isRefreshing) {
    return 'No';
  }
  if (!targets) {
    return 'Yes';
  }
  const labels = [];
  if (targets.phaser) labels.push('Phaser PO');
  if (targets.ccc) labels.push('CCC PO');
  if (targets.suppliers) labels.push('Qoblox suppliers');
  return labels.length ? labels.join(', ') : 'No';
}

const INCOMING_STOCK_REFRESH_KEYS = new Set([
  'purchase-orders',
  'purchase-orders-phaser',
  'purchase-orders-ccc',
  'qoblox-suppliers'
]);

export const INVENTORY_PLANNER_REFRESH_KEY = 'inventory-planner';

const SHOPIFY_STORE_DOMAIN_BY_KEY = {
  'phaser-fpv': 'phaser-fpv.myshopify.com',
  'central-coast-coffee': 'central-coast-coffee.myshopify.com',
  'porlex-australia': 'porlex-australia.myshopify.com',
  'ace176-c7': 'ace176-c7.myshopify.com'
};

function parseTimestampMs(value) {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function pickLatestTimestampMs(values = []) {
  const parsed = values
    .map((value) => parseTimestampMs(value))
    .filter((ms) => ms != null);
  return parsed.length ? Math.max(...parsed) : null;
}

/** True when job activity timestamps fall at/after this refresh run started. */
export function isJobActivitySinceRefresh(job, thresholdMs, fields = ['updatedAt', 'completedAt', 'startedAt']) {
  if (!job || !Number.isFinite(thresholdMs)) return false;
  const activityMs = pickLatestTimestampMs(fields.map((field) => job[field]));
  return activityMs != null && activityMs >= thresholdMs;
}

/**
 * Resolve refresh picker status for a planner sales sync job.
 * Returns null when the job state is stale (prior run) so polling continues.
 */
export function resolvePlannerSyncJobRefreshStatus(job, thresholdMs) {
  if (!job) return null;

  const syncStatus = String(job.status || '').trim();
  const inProcess = job.inProcess === true;
  const recent = isJobActivitySinceRefresh(job, thresholdMs);

  if (syncStatus === 'running' || inProcess) {
    return 'refreshing';
  }
  if (syncStatus === 'paused' || syncStatus === 'idle') {
    return recent ? 'failed' : null;
  }
  if (syncStatus === 'completed') {
    return recent ? 'complete' : null;
  }
  if (syncStatus === 'failed' || syncStatus === 'interrupted') {
    return recent ? 'failed' : null;
  }
  return null;
}

const PLANNER_ASYNC_REFRESH_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Resolve planner async refresh target when kickoff returns before job timestamps update.
 * Falls back to running/timeout instead of leaving started_background stuck forever.
 */
export function resolvePlannerAsyncRefreshTarget(job, thresholdMs, {
  currentStatus = 'queued',
  runStartedAtMs,
  nowMs = Date.now(),
  timeoutMs = PLANNER_ASYNC_REFRESH_TIMEOUT_MS,
  resolveStatus = resolvePlannerSyncJobRefreshStatus
} = {}) {
  const resolved = resolveStatus(job, thresholdMs);
  if (resolved) return resolved;

  if (job && (String(job.status || '').trim() === 'running' || job.inProcess === true)) {
    return 'refreshing';
  }

  if (currentStatus === 'started_background' || currentStatus === 'queued') {
    if (Number.isFinite(runStartedAtMs) && (nowMs - runStartedAtMs) > timeoutMs) {
      return 'failed';
    }
    return 'refreshing';
  }

  return currentStatus;
}

/**
 * Resolve refresh picker status for Lightspeed cost backfill.
 * Returns null when the job state is stale (prior run) so polling continues.
 */
export function resolvePlannerCostBackfillRefreshStatus(payload, thresholdMs) {
  if (!payload) return null;

  const jobStatus = String(payload.jobStatus || '').trim();
  const inProcess = payload.inProcess === true;
  const recent = isJobActivitySinceRefresh(payload, thresholdMs, ['jobUpdatedAt', 'completedAt', 'jobStartedAt']);

  if (jobStatus === 'running' || inProcess) {
    return 'refreshing';
  }
  if (jobStatus === 'paused' || jobStatus === 'idle') {
    return recent ? 'failed' : null;
  }
  if (jobStatus === 'completed') {
    return recent ? 'complete' : null;
  }
  if (jobStatus === 'failed' || jobStatus === 'interrupted') {
    return recent ? 'failed' : null;
  }
  return null;
}

/** Same kickoff grace/timeout behaviour as sales sync, for cost backfill jobs. */
export function resolvePlannerCostBackfillAsyncTarget(payload, thresholdMs, options = {}) {
  return resolvePlannerAsyncRefreshTarget(payload, thresholdMs, {
    ...options,
    resolveStatus: (job, threshold) => resolvePlannerCostBackfillRefreshStatus(job, threshold)
  });
}

/** Supplier SKU sync uses the same job status shape as cost backfill. */
export function resolvePlannerSupplierSkuSyncAsyncTarget(payload, thresholdMs, options = {}) {
  return resolvePlannerAsyncRefreshTarget(payload, thresholdMs, {
    ...options,
    resolveStatus: (job, threshold) => resolvePlannerCostBackfillRefreshStatus(job, threshold)
  });
}

/** True when selected targets show a refresh attempt at/after this UI run started. */
export function refreshTargetsStartedSince(cacheStatus, startedAtMs, selectedStoresList = []) {
  if (!cacheStatus || !Number.isFinite(startedAtMs)) return false;
  const thresholdMs = startedAtMs - 2000;

  const selectedIncoming = selectedStoresList.filter((key) => INCOMING_STOCK_REFRESH_KEYS.has(key));
  if (selectedIncoming.length > 0) {
    const poAttemptMs = parseTimestampMs(cacheStatus.poRefresh?.lastAttempt);
    if (poAttemptMs != null && poAttemptMs >= thresholdMs) return true;
  }

  if (selectedStoresList.includes(INVENTORY_PLANNER_REFRESH_KEY)) {
    const plannerAttemptMs = parseTimestampMs(cacheStatus.plannerRefresh?.lastAttempt);
    if (plannerAttemptMs != null && plannerAttemptMs >= thresholdMs) return true;
  }

  const selectedShopify = selectedStoresList.filter(
    (key) => !INCOMING_STOCK_REFRESH_KEYS.has(key) && key !== INVENTORY_PLANNER_REFRESH_KEY
  );
  if (selectedShopify.length > 0) {
    if (selectedShopify.some((key) => {
      const domain = SHOPIFY_STORE_DOMAIN_BY_KEY[key];
      const store = cacheStatus.stores?.find((entry) => entry.store === domain);
      return !!store?.isRefreshing;
    })) {
      return true;
    }
    const lastRefreshMs = parseTimestampMs(cacheStatus.lastRefresh);
    if (lastRefreshMs != null && lastRefreshMs >= thresholdMs) return true;
  }

  return false;
}

/**
 * Poll state for a user-initiated refresh.
 * Returns pending | active | complete | failed.
 */
export function evaluateRefreshPollStatus(cacheStatus, {
  startedAtMs,
  seenBackendActive = false,
  selectedStoresList = [],
  nowMs = Date.now(),
  startGraceMs = 15000
} = {}) {
  if (!cacheStatus) return 'pending';

  if (cacheStatus.isRefreshing) return 'active';

  const acknowledged = seenBackendActive
    || refreshTargetsStartedSince(cacheStatus, startedAtMs, selectedStoresList);

  if (!acknowledged) {
    return (nowMs - startedAtMs) < startGraceMs ? 'pending' : 'failed';
  }

  return 'complete';
}

/** Success toast after a selective refresh completes. */
export function buildRefreshCompleteMessage(cacheStatus, selectedStoresList = []) {
  const shopifyStores = selectedStoresList.filter((key) => !INCOMING_STOCK_REFRESH_KEYS.has(key) && key !== INVENTORY_PLANNER_REFRESH_KEY);
  const incomingStores = selectedStoresList.filter((key) => INCOMING_STOCK_REFRESH_KEYS.has(key));
  const includesPlanner = selectedStoresList.includes(INVENTORY_PLANNER_REFRESH_KEY);
  const parts = [];

  if (shopifyStores.length > 0) {
    parts.push(`${cacheStatus?.totalProducts ?? 0} products indexed`);
  }
  if (incomingStores.some((key) => key === 'purchase-orders' || key === 'purchase-orders-phaser')) {
    parts.push(`${cacheStatus?.poCache?.phaser?.orderCount ?? 0} Phaser POs`);
  }
  if (incomingStores.some((key) => key === 'purchase-orders' || key === 'purchase-orders-ccc')) {
    parts.push(`${cacheStatus?.poCache?.ccc?.orderCount ?? 0} CCC POs`);
  }
  if (incomingStores.some((key) => key === 'purchase-orders' || key === 'qoblox-suppliers')) {
    parts.push(`${cacheStatus?.qobloxSuppliers?.count ?? 0} suppliers`);
  }
  if (includesPlanner) {
    const durationSec = cacheStatus?.plannerRefresh?.lastDurationMs
      ? `${(cacheStatus.plannerRefresh.lastDurationMs / 1000).toFixed(0)}s`
      : 'done';
    parts.push(`planner cycle ${durationSec}`);
  }

  if (parts.length === 0) {
    return 'Refresh completed.';
  }

  return `Refresh completed (${parts.join(', ')}).`;
}

/** Progress toast while a refresh is running. */
export function buildRefreshProgressMessage(cacheStatus) {
  if (isPlannerCycleRefreshing(cacheStatus)) {
    return 'Refreshing Inventory planner cycle (ingest, forecast, optimiser)…';
  }
  const poTargets = formatActivePoRefreshTargets(cacheStatus);
  if (poTargets !== 'No') {
    return `Refreshing Incoming Stock: ${poTargets}…`;
  }
  if (cacheStatus?.stores?.some((store) => store.isRefreshing)) {
    const active = cacheStatus.stores.filter((store) => store.isRefreshing).map((store) => store.displayName || store.store);
    return `Refreshing ${active.join(', ')}…`;
  }
  return 'Refreshing database caches…';
}

/** Human-readable active refresh summary for cache status modal. */
export function formatActiveRefreshTargets(cacheStatus) {
  const labels = [];
  const poTargets = formatActivePoRefreshTargets(cacheStatus);
  if (poTargets !== 'No') labels.push(poTargets);
  if (isPlannerCycleRefreshing(cacheStatus)) labels.push('Inventory planner');
  if (cacheStatus?.stores?.some((store) => store.isRefreshing)) {
    labels.push(...cacheStatus.stores.filter((store) => store.isRefreshing).map((store) => store.displayName || store.store));
  }
  if (!labels.length) return 'No';
  return labels.join(', ');
}

/** True when reopening the refresh picker should restore an in-flight run, not reset it. */
export function shouldRestoreRefreshPickerRun({ isRefreshingCache, refreshRun }) {
  return !!isRefreshingCache
    && Array.isArray(refreshRun?.selectedTargets)
    && refreshRun.selectedTargets.length > 0;
}

/** Restore checkbox map from an active refresh run without mutating run/status state. */
export function buildRefreshStoreSelection(selectedTargets, emptySelectionTemplate) {
  const selection = { ...emptySelectionTemplate };
  Object.keys(selection).forEach((key) => {
    selection[key] = false;
  });
  (selectedTargets || []).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(selection, key)) {
      selection[key] = true;
    }
  });
  return selection;
}
